/**
 * 라이브 편성표 · 엔드 페이지 공용: 다시보기(VOD), 예약 알림 앱 연동
 */
import { isAndroid, isIOS, isSbsApp } from './passUtil';

function getSbsAppVersion() {
  if (typeof window === 'undefined') return null;
  const match = navigator.userAgent.match(/SBSApp\/([\d.]+)/);
  return match ? match[1] : null;
}

function isAppVersionLower(current, target) {
  const currentParts = String(current).split('.').map(Number);
  const targetParts = String(target).split('.').map(Number);
  for (let i = 0; i < targetParts.length; i += 1) {
    const curr = currentParts[i] || 0;
    const targ = targetParts[i] || 0;
    if (curr < targ) return true;
    if (curr > targ) return false;
  }
  return false;
}

export function formatAlarmDate(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}`;
}

/** 앱 toggleOnairAlarmActive · intent date — KST (편성 startTimestamp 와 동일 기준) */
export function formatAlarmDateKst(ts) {
  if (ts == null || ts === '') return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(typeof ts === 'number' ? ts : ts));
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const mo = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const h = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const min = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${y}-${mo}-${day} ${h}:${min}`;
}

/** localStorage alarms — 3일 지난 키 제외 (엔드·편성표 공용) */
export function filterRecentAlarms(parsedAlarms, maxAgeDays = 3) {
  const currentDate = new Date();
  return Object.entries(parsedAlarms || {}).reduce((acc, [key, value]) => {
    const [, dateString] = key.split('_');
    if (!dateString) return acc;
    const alarmDate = new Date(dateString.split(' ')[0]);
    const daysDiff =
      (currentDate.getTime() - alarmDate.getTime()) / (1000 * 3600 * 24);
    if (daysDiff < maxAgeDays) acc[key] = value;
    return acc;
  }, {});
}

/**
 * SSR 하이드레이션 후 클라 마운트 시 localStorage 에서 alarms 복원.
 * useState 초기화만으로는 서버 `{}` 가 그대로 붙어 새로고침 시 아이콘이 꺼져 보임.
 */
export function readAlarmsFromStorage() {
  if (typeof window === 'undefined') return {};
  try {
    return filterRecentAlarms(JSON.parse(localStorage.getItem('alarms') || '{}'));
  } catch {
    return {};
  }
}

/** 앱 브릿지 isActive — 문자열 "false" 등 정규화 */
export function normalizeAlarmIsActive(isActive) {
  if (isActive === false || isActive === 0) return false;
  if (typeof isActive === 'string') {
    const s = isActive.trim().toLowerCase();
    if (s === 'false' || s === '0' || s === '') return false;
  }
  return !!isActive;
}

/** UI 표시용 — localStorage 는 그대로, 로그아웃(jwt 없음) 시 아이콘만 비활성 */
export function isAlarmActiveForDisplay(alarmsMap, channelId, alarmDate, jwtToken) {
  if (!jwtToken) return false;
  const key = `${channelId}_${alarmDate}`;
  return !!alarmsMap?.[key];
}

/** @typedef {'liveEnd' | 'schedule'} LiveSchedulePageType */

/** @param {LiveSchedulePageType} pageType @param {'alarm' | 'replay'} action */
export function getScheduleEngagementValue1(pageType, action) {
  if (pageType === 'liveEnd') {
    return action === 'alarm' ? '라이브/편성표/예약' : '라이브/편성표/다시보기';
  }
  return action === 'alarm' ? '편성표/예약' : '편성표/다시보기';
}

function pushLiveScheduleEngagement({
  channelId,
  channelName,
  programId,
  title,
  value1,
}) {
  if (typeof window === 'undefined') return;
  window.__SBSLogModuleDataLayer = window.__SBSLogModuleDataLayer || [];
  window.__SBSLogModuleDataLayer.push({
    type: 'engagement',
    option: {
      type: '기타(E)',
      channelId: channelId || '', // 채널식별자
      channelName: channelName || '', // 채널이름
      programId: programId || '', // 프로그램식별자
      programTitle: title || '', // 프로그램제목
      contentId: programId,
      contentTitle: title,
      value1,
      value2: '',
      value3: '',
      value4: '',
      value5: '',
    },
  });
}

/** 편성 다시보기(VOD) — 타임라인 program / 레거시 program 필드 모두 허용 */
export function openLivePlanReplay(program, channelId, { channelName = '', pageType = 'schedule' } = {}) {
  if (typeof window === 'undefined') return;
  const vodUrl = program.vodUrl || program.vod_url || '';
  const programId = program.programId || program.program_id || '';
  const title = program.title || '';

  pushLiveScheduleEngagement({
    channelId,
    channelName,
    programId,
    title,
    value1: getScheduleEngagementValue1(pageType, 'replay'),
  });
  const encodedTitle = encodeURIComponent(title);
  const isRadio = ['S17', 'S18', 'S19'].includes(String(channelId));

  let reLink = '';

  if (!isSbsApp()) {
    const appLink = isRadio
      ? isIOS()
        ? 'gorealra://'
        : 'intent://main?type=link#Intent;scheme=gorealra;package=com.appgate.gorealra;S.market_referrer=sharepage;end'
      : programId
        ? isIOS()
          ? `siapp://programhome?programid=${programId}&programtitle=${encodedTitle}&tab=vod`
          : `intent://programhome?programid=${programId}&programtitle=${encodedTitle}&tab=vod#Intent;scheme=siapp;package=kr.co.sbs.videoplayer;end`
        : vodUrl;

    const appStoreLink = isRadio
      ? isIOS()
        ? 'https://apps.apple.com/kr/app/id340072873'
        : 'https://play.google.com/store/apps/details?id=com.appgate.gorealra'
      : isIOS()
        ? 'https://itunes.apple.com/kr/app/id1020136055?mt=8'
        : 'https://play.google.com/store/apps/details?id=kr.co.sbs.videoplayer';

    const handleAppInstallCheck = (callback) => {
      const installCheck = window.setInterval(() => {
        if (document.hidden) {
          window.clearInterval(installCheck);
          window.clearTimeout(fallbackTimer);
        }
      }, 200);
      const fallbackTimer = window.setTimeout(() => {
        callback();
        window.clearInterval(installCheck);
      }, 3000);
    };

    if (isIOS()) {
      handleAppInstallCheck(() => {
        reLink = appStoreLink;
      });
    } else if (isAndroid()) {
      handleAppInstallCheck(() => {
        reLink = 'https://play.google.com/store/apps/details?id=kr.co.sbs.videoplayer';
      });
    } else {
      reLink = vodUrl;
    }

    if (!reLink) reLink = appLink;
  } else {
    const appVersion = getSbsAppVersion();
    const isLowVersionCheck = appVersion ? isAppVersionLower(appVersion, '3.0.0') : false;

    if (isLowVersionCheck) {
      reLink = vodUrl;
    } else if (isRadio) {
      reLink = isIOS()
        ? 'gorealra://'
        : 'intent://main?type=link#Intent;scheme=gorealra;package=com.appgate.gorealra;S.market_referrer=sharepage;end';
    } else if (programId) {
      reLink = isIOS()
        ? `siapp://programhome?programid=${programId}&programtitle=${encodedTitle}&tab=vod`
        : `intent://programhome?programid=${programId}&programtitle=${encodedTitle}&tab=vod#Intent;scheme=siapp;package=kr.co.sbs.videoplayer;end`;
    } else {
      reLink = vodUrl;
    }
  }

  if (reLink) {
    window.location.href = reLink;
  }
}

export function openLivePlanAlarmFromSchedule({
  channelId,
  channelName,
  programId,
  title,
  alarmDate,
  circleImage,
  jwtToken,
  isAlarmActive,
  onToggle,
  pageType = 'schedule',
}) {
  if (typeof window === 'undefined') return;
  const circle = circleImage || '';
  /** 전체 편성표(schedule) Android 만 구 라이브 intent date 규칙 적용 */
  const androidScheduleIntent = isAndroid() && pageType === 'schedule';

  pushLiveScheduleEngagement({
    channelId,
    channelName,
    programId,
    title,
    value1: getScheduleEngagementValue1(pageType, 'alarm'),
  });

  if (isSbsApp()) {
    if (!jwtToken) {
      if (isAndroid()) {
        const dateParam = androidScheduleIntent
          ? alarmDate
          : encodeURIComponent(alarmDate);
        window.location.href = `intent://login?type=schedule&channelid=${channelId}&date=${dateParam}&img=${encodeURIComponent(
          circle
        )}#Intent;scheme=siapp;package=kr.co.sbs.videoplayer;end`;
      } else {
        window.location.href = `siapp://login?type=schedule&channelid=${channelId}&date=${encodeURIComponent(
          alarmDate
        )}&img=${encodeURIComponent(circle)}`;
      }
      return;
    }
    if (isAndroid()) {
      const dateParam = androidScheduleIntent
        ? alarmDate
        : encodeURIComponent(alarmDate);
      window.location.href = `intent://schedule?channelid=${channelId}&date=${dateParam}&img=${encodeURIComponent(
        circle
      )}&programtitle=${encodeURIComponent(title || '')}#Intent;scheme=siapp;package=kr.co.sbs.videoplayer;end`;
    } else {
      window.location.href = `siapp://schedule?channelid=${channelId}&date=${encodeURIComponent(
        alarmDate
      )}&img=${encodeURIComponent(circle)}&programtitle=${encodeURIComponent(title || '')}`;
    }
  } else if (confirm(`라이브 예약은 앱에서만 가능합니다. 'SBS play' 에서 열겠습니까?`)) {
    window.location.href = 'siapp://home?tab=home';
  }
}
